// Library Methods
import axios from "axios";

// Utils
import {
  config,
  injectBearer,
  webServiceUrl,
} from "src/utils/services/servicesHelper";
import { getLocalDate, sortByOldestDate } from "src/utils/date/dateHelper";

// Types
import {
  AddExamSectionType,
  AccessInfoType,
  ExamCommentsType,
  ExternalExamChartType,
  ReportInfoType,
  ExamType,
  ExternalExamType,
  ProgressionAnalysisType,
  GPAProcessInfoType,
  AddLensSectionType,
  IndividualExternalExamType,
  PositionalNoteType,
  ProgressionExamSummaryType,
} from "./types";
import { ExamCardSectionNameType } from "src/constants/enums";

const getExams = async (
  accessToken: string,
  clinicId: string,
  patientId: string,
  signal: AbortSignal
): Promise<Array<ExamType>> => {
  try {
    const { data } = await axios.get(
      `${webServiceUrl}/clinics/${clinicId}/patients/${patientId}/exams`,
      config(accessToken, signal)
    );
    return data?.data;
  } catch (error) {
    throw error;
  }
};

const getExamById = async (
  accessToken: string | null,
  clinicId: string,
  patientId: string,
  examId: string,
  signal: AbortSignal
): Promise<ExamType> => {
  try {
    const { data } = await axios.get(
      `${webServiceUrl}/clinics/${clinicId}/patients/${patientId}/exams/${examId}`,
      config(accessToken, signal)
    );
    return data?.data;
  } catch (error) {
    throw error;
  }
};

const getRecentlyCompletedExams = async (
  accessToken: string,
  clinicId: string,
  params: { recentFilterDate: string },
  signal: AbortSignal
): Promise<Array<ExamType>> => {
  try {
    const { data } = await axios.get(
      `${webServiceUrl}/clinics/${clinicId}/dashboard/recently-completed-exams`,
      {
        headers: { Authorization: injectBearer(accessToken) },
        params,
        signal,
      }
    );
    return data?.data;
  } catch (error) {
    throw error;
  }
};

const getRecentlyCompletedProgressionAnalyses = async (
  accessToken: string,
  clinicId: string,
  params: { recentFilterDate: string },
  signal: AbortSignal
): Promise<Array<ProgressionAnalysisType>> => {
  try {
    const { data } = await axios.get(
      `${webServiceUrl}/clinics/${clinicId}/dashboard/recently-completed-progression-analyses`,
      {
        headers: { Authorization: injectBearer(accessToken) },
        params,
        signal,
      }
    );
    return data?.data;
  } catch (error) {
    throw error;
  }
};

const getScheduledExams = async (
  accessToken: string,
  clinicId: string,
  params: { startDate: string; endDate: string },
  signal: AbortSignal
): Promise<Array<ExamType>> => {
  try {
    const { data } = await axios.get(
      `${webServiceUrl}/clinics/${clinicId}/dashboard/scheduled-exams`,
      {
        headers: { Authorization: injectBearer(accessToken) },
        params,
        signal: signal,
      }
    );
    return data?.data;
  } catch (error) {
    throw error;
  }
};

const getLinkedExams = async (
  accessToken: string,
  clinicId: string,
  params: { startDate: string; endDate: string },
  signal: AbortSignal
) => {
  try {
    const { data } = await axios.get(
      `${webServiceUrl}/clinics/${clinicId}/dashboard/linked-exams`,
      {
        headers: { Authorization: injectBearer(accessToken) },
        params,
        signal: signal,
      }
    );
    return data?.data;
  } catch (error) {
    throw error;
  }
};

const addExam = async (
  accessToken: string,
  clinicId: string,
  patientId: string,
  examData: { scheduledDate: string } & {
    [key in ExamCardSectionNameType]?: Array<
      AddExamSectionType | AddLensSectionType
    >;
  }
): Promise<ExamType> => {
  try {
    const { data } = await axios.post(
      `${webServiceUrl}/clinics/${clinicId}/patients/${patientId}/exams`,
      examData,
      config(accessToken)
    );
    return data?.data;
  } catch (error) {
    throw error;
  }
};

const updateExam = async (
  accessToken: string,
  clinicId: string,
  patientId: string,
  examId: string,
  examData: { scheduledDate: string } & {
    [key in ExamCardSectionNameType]?: Array<
      AddExamSectionType | AddLensSectionType
    >;
  }
): Promise<ExamType> => {
  try {
    const { data } = await axios.put(
      `${webServiceUrl}/clinics/${clinicId}/patients/${patientId}/exams/${examId}`,
      examData,
      config(accessToken)
    );
    return data?.data;
  } catch (error) {
    throw error;
  }
};

const getAllProgressionAnalyses = async (
  accessToken: string,
  clinicId: string,
  patientId: string,
  clinicTimezone: string | null
): Promise<Array<ProgressionAnalysisType>> => {
  try {
    const { data } = await axios.get(
      `${webServiceUrl}/clinics/${clinicId}/patients/${patientId}/progression-analysis`,
      config(accessToken)
    );

    return data?.data?.map((progressionAnalysis: ProgressionAnalysisType) => {
      // get all completion dates from both exam types
      const allExamDates = [
        ...progressionAnalysis?.exams.map((e) => ({
          completionDate: e?.completionDate,
        })),
        ...progressionAnalysis?.externalExams.map((e) => ({
          completionDate:
            e?.completionDate ?? e?.data?.completionDate ?? undefined,
        })),
      ];

      // get exams with a valid completion date
      const filteredExams = allExamDates.filter((exam) =>
        Boolean(exam.completionDate)
      );
      // sort by completion date
      const sortedArray = sortByOldestDate(filteredExams, "completionDate");

      // get earliest and latest completion dates
      const earliest =
        sortedArray.length > 0 ? sortedArray[0]?.completionDate : null;
      const latest =
        sortedArray.length > 0
          ? sortedArray[sortedArray.length - 1]?.completionDate
          : null;

      return {
        ...progressionAnalysis,
        earliestCompletionLocalDate: earliest
          ? getLocalDate(earliest, clinicTimezone)
          : "N/A",
        latestCompletionLocalDate: latest
          ? getLocalDate(latest, clinicTimezone)
          : "N/A",
      };
    });
  } catch (error) {
    throw error;
  }
};

const getProgressionAnalysisById = async (
  accessToken: string | null,
  clinicId: string,
  patientId: string,
  analysisId: string
): Promise<ProgressionAnalysisType> => {
  try {
    const { data } = await axios.get(
      `${webServiceUrl}/clinics/${clinicId}/patients/${patientId}/progression-analysis/${analysisId}`,
      config(accessToken)
    );
    return data?.data;
  } catch (error) {
    throw error;
  }
};

const getExternalExams = async (
  accessToken: string,
  clinicId: string,
  patientId: string,
  type?: string
): Promise<Array<ExternalExamType>> => {
  try {
    const { data } = await axios.get(
      `${webServiceUrl}/clinics/${clinicId}/patients/${patientId}/external-exams${type ? `?type=${type}` : ""}`,
      config(accessToken)
    );
    return data?.data;
  } catch (error) {
    throw error;
  }
};

const getProgressionExams = async (
  accessToken: string,
  clinicId: string,
  patientId: string,
  eye?: string
): Promise<Array<ProgressionExamSummaryType>> => {
  try {
    const { data } = await axios.get(
      `${webServiceUrl}/clinics/${clinicId}/patients/${patientId}/exams/for-progression${eye ? `?eye=${eye}` : ""}`,
      config(accessToken)
    );
    return data?.data;
  } catch (error) {
    throw error;
  }
};

const getProgressionExternalExams = async (
  accessToken: string,
  clinicId: string,
  patientId: string,
  eye?: string
): Promise<Array<ProgressionExamSummaryType>> => {
  try {
    const { data } = await axios.get(
      `${webServiceUrl}/clinics/${clinicId}/patients/${patientId}/external-exams/for-progression${eye ? `?eye=${eye}` : ""}`,
      config(accessToken)
    );
    return data?.data;
  } catch (error) {
    throw error;
  }
};

const getExternalExamById = async (
  accessToken: string | null,
  clinicId: string,
  patientId: string,
  examId: string,
  signal: AbortSignal
): Promise<IndividualExternalExamType> => {
  try {
    const { data } = await axios.get(
      `${webServiceUrl}/clinics/${clinicId}/patients/${patientId}/external-exams/${examId}`,
      config(accessToken, signal)
    );
    return data?.data;
  } catch (error) {
    throw error;
  }
};

const getProgressionAnalysisForExternalExams = async (
  accessToken: string,
  clinicId: string,
  patientId: string,
  externalExamIds: Array<string>
): Promise<Array<ProgressionAnalysisType>> => {
  try {
    const promiseRes = await Promise.all(
      externalExamIds.map((externalExamId) =>
        axios.get(
          `${webServiceUrl}/clinics/${clinicId}/patients/${patientId}/external-exams/${externalExamId}/progression-analysis`,
          config(accessToken)
        )
      )
    );
    return promiseRes.map((res) => res?.data?.data).flat();
  } catch (error) {
    throw error;
  }
};

const deleteExternalExam = async (
  accessToken: string,
  clinicId: string,
  patientId: string,
  externalExamIds: Array<string>
) => {
  try {
    await Promise.all(
      externalExamIds.map((externalExamId) =>
        axios.delete(
          `${webServiceUrl}/clinics/${clinicId}/patients/${patientId}/external-exams/${externalExamId}`,
          config(accessToken)
        )
      )
    );
  } catch (error) {
    throw error;
  }
};

const favoriteExternalExam = async (
  accessToken: string,
  clinicId: string,
  patientId: string,
  externalExamId: string,
  isFavorite: boolean
): Promise<ExternalExamType> => {
  try {
    const { data } = await axios.put(
      `${webServiceUrl}/clinics/${clinicId}/patients/${patientId}/external-exams/${externalExamId}`,
      { id: externalExamId, isFavorite },
      config(accessToken)
    );
    return data?.data;
  } catch (error) {
    throw error;
  }
};

const deleteProgressionAnalysis = async (
  accessToken: string,
  clinicId: string,
  patientId: string,
  analysisIds: Array<string>
) => {
  try {
    await Promise.all(
      analysisIds.map((analysisId) =>
        axios.delete(
          `${webServiceUrl}/clinics/${clinicId}/patients/${patientId}/progression-analysis/${analysisId}`,
          config(accessToken)
        )
      )
    );
  } catch (error) {
    throw error;
  }
};

const getProgressionAnalysisForExams = async (
  accessToken: string,
  clinicId: string,
  patientId: string,
  examIds: Array<string>
): Promise<Array<ProgressionAnalysisType>> => {
  try {
    const promiseRes = await Promise.all(
      examIds.map((examId) =>
        axios.get(
          `${webServiceUrl}/clinics/${clinicId}/patients/${patientId}/exams/${examId}/progression-analysis`,
          config(accessToken)
        )
      )
    );
    return promiseRes.map((res) => res?.data?.data).flat();
  } catch (error) {
    throw error;
  }
};

const deleteExam = async (
  accessToken: string,
  clinicId: string,
  patientId: string,
  examIds: Array<string>
) => {
  try {
    await Promise.all(
      examIds.map((examId) =>
        axios.delete(
          `${webServiceUrl}/clinics/${clinicId}/patients/${patientId}/exams/${examId}`,
          config(accessToken)
        )
      )
    );
  } catch (error) {
    throw error;
  }
};

const transferExam = async (
  accessToken: string,
  clinicId: string,
  patientId: string,
  examId: string,
  newPatientId: string
) => {
  try {
    const { data } = await axios.post(
      `${webServiceUrl}/clinics/${clinicId}/patients/${patientId}/exams/${examId}/transfer/${newPatientId}`,
      null,
      config(accessToken)
    );
    return data?.data;
  } catch (error) {
    throw error;
  }
};

const getExamAccessInfo = async (
  accessToken: string,
  clinicId: string,
  patientId: string,
  examId: string,
  fileType = "pdf",
  signal: AbortSignal
): Promise<AccessInfoType> => {
  try {
    const { data } = await axios.get(
      `${webServiceUrl}/clinics/${clinicId}/patients/${patientId}/exams/${examId}/file-management/${fileType}`,
      config(accessToken, signal)
    );
    return data?.data;
  } catch (error) {
    throw error;
  }
};

const generateExamReportFile = async (
  accessToken: string,
  clinicId: string,
  patientId: string,
  examId: string,
  fileType = "pdf",
  signal: AbortSignal
) => {
  try {
    const { data } = await axios.post(
      `${webServiceUrl}/clinics/${clinicId}/patients/${patientId}/exams/${examId}/regenerate-report/${fileType}`,
      null,
      config(accessToken, signal)
    );
    return data?.data;
  } catch (error) {
    throw error;
  }
};

const getExamReportInfo = async (
  accessToken: string,
  clinicId: string,
  patientId: string,
  examId: string,
  signal: AbortSignal
): Promise<Array<ReportInfoType>> => {
  try {
    const { data } = await axios.get(
      `${webServiceUrl}/clinics/${clinicId}/patients/${patientId}/exams/${examId}/report-info`,
      config(accessToken, signal)
    );
    return data?.data;
  } catch (error) {
    throw error;
  }
};

const getReportSectionData = async (
  accessToken: string | null,
  clinicId: string,
  patientId: string,
  examId: string,
  sectionId: string,
  signal: AbortSignal
): Promise<string> => {
  try {
    const { data } = await axios.get(
      `${webServiceUrl}/clinics/${clinicId}/patients/${patientId}/exams/${examId}/sections/${sectionId}/submission`,
      config(accessToken, signal)
    );
    return data?.data;
  } catch (error) {
    throw error;
  }
};

const getExternalExamCharts = async (
  accessToken: string | null,
  clinicId: string,
  patientId: string,
  externalExamId: string,
  signal: AbortSignal
): Promise<Array<ExternalExamChartType>> => {
  try {
    const { data } = await axios.get(
      `${webServiceUrl}/clinics/${clinicId}/patients/${patientId}/external-exams/${externalExamId}/charts`,
      config(accessToken, signal)
    );
    return data?.data?.charts ?? [];
  } catch (error) {
    return [];
  }
};

const getReportSectionComments = async (
  accessToken: string | null,
  clinicId: string,
  patientId: string,
  examId: string,
  sectionId: string,
  signal: AbortSignal
): Promise<Array<ExamCommentsType>> => {
  try {
    const { data } = await axios.get(
      `${webServiceUrl}/clinics/${clinicId}/patients/${patientId}/exams/${examId}/sections/${sectionId}/comments`,
      config(accessToken, signal)
    );
    return data?.data;
  } catch (error) {
    throw error;
  }
};

const updateProgressionBaseline = async (
  accessToken: string | null,
  clinicId: string,
  patientId: string,
  analysisId: string,
  // Must combine to exactly 2 (or both empty, to clear the baselines).
  baselineExamIds: Array<string>,
  baselineExternalExamIds: Array<string>
): Promise<ProgressionAnalysisType> => {
  try {
    const { data } = await axios.put(
      `${webServiceUrl}/clinics/${clinicId}/patients/${patientId}/progression-analysis/${analysisId}/baselines`,
      { baselineExamIds, baselineExternalExamIds },
      config(accessToken)
    );
    return data?.data;
  } catch (error) {
    throw error;
  }
};

const getProgressionAccessInfo = async (
  accessToken: string,
  clinicId: string,
  patientId: string,
  analysisId: string,
  fileType = "pdf",
  signal: AbortSignal
): Promise<AccessInfoType> => {
  try {
    const { data } = await axios.get(
      `${webServiceUrl}/clinics/${clinicId}/patients/${patientId}/progression-analysis/${analysisId}/file-management/${fileType}`,
      config(accessToken, signal)
    );
    return data?.data;
  } catch (error) {
    throw error;
  }
};

const generateProgressionReportFile = async (
  accessToken: string,
  clinicId: string,
  patientId: string,
  analysisId: string,
  fileType = "pdf",
  signal: AbortSignal
) => {
  try {
    const { data } = await axios.post(
      `${webServiceUrl}/clinics/${clinicId}/patients/${patientId}/progression-analysis/${analysisId}/regenerate-report/${fileType}`,
      null,
      config(accessToken, signal)
    );
    return data?.data;
  } catch (error) {
    throw error;
  }
};

const getProgressionReportInfo = async (
  accessToken: string,
  clinicId: string,
  patientId: string,
  analysisId: string,
  signal: AbortSignal
): Promise<Array<ReportInfoType>> => {
  try {
    const { data } = await axios.get(
      `${webServiceUrl}/clinics/${clinicId}/patients/${patientId}/progression-analysis/${analysisId}/report-info`,
      config(accessToken, signal)
    );
    return data?.data;
  } catch (error) {
    throw error;
  }
};

const addExamReportComment = async (
  accessToken: string,
  clinicId: string,
  patientId: string,
  examId: string,
  sectionId: string,
  commentData: { content: string; conditions?: string }
): Promise<void> => {
  try {
    const { data } = await axios.post(
      `${webServiceUrl}/clinics/${clinicId}/patients/${patientId}/exams/${examId}/sections/${sectionId}/comments`,
      commentData,
      config(accessToken)
    );
    return data?.data;
  } catch (error) {
    throw error;
  }
};

const updateExamReportComment = async (
  accessToken: string,
  clinicId: string,
  patientId: string,
  examId: string,
  sectionId: string,
  commentId: string,
  commentData: { content: string; conditions?: string }
): Promise<void> => {
  try {
    const { data } = await axios.put(
      `${webServiceUrl}/clinics/${clinicId}/patients/${patientId}/exams/${examId}/sections/${sectionId}/comments/${commentId}`,
      commentData,
      config(accessToken)
    );
    return data?.data;
  } catch (error) {
    throw error;
  }
};

const getProgressionAnalysisComments = async (
  accessToken: string | null,
  clinicId: string,
  patientId: string,
  progressionAnalysisId: string,
  signal: AbortSignal
): Promise<Array<ExamCommentsType>> => {
  try {
    const { data } = await axios.get(
      `${webServiceUrl}/clinics/${clinicId}/patients/${patientId}/progression-analysis/${progressionAnalysisId}/comments`,
      config(accessToken, signal)
    );
    return data?.data;
  } catch (error) {
    throw error;
  }
};

const addProgressionAnalysisComment = async (
  accessToken: string,
  clinicId: string,
  patientId: string,
  progressionAnalysisId: string,
  commentData: { content: string; conditions?: string }
): Promise<void> => {
  try {
    const { data } = await axios.post(
      `${webServiceUrl}/clinics/${clinicId}/patients/${patientId}/progression-analysis/${progressionAnalysisId}/comments`,
      commentData,
      config(accessToken)
    );
    return data?.data;
  } catch (error) {
    throw error;
  }
};

const updateProgressionAnalysisComment = async (
  accessToken: string,
  clinicId: string,
  patientId: string,
  progressionAnalysisId: string,
  commentId: string,
  commentData: { content: string; conditions?: string }
): Promise<void> => {
  try {
    const { data } = await axios.put(
      `${webServiceUrl}/clinics/${clinicId}/patients/${patientId}/progression-analysis/${progressionAnalysisId}/comments/${commentId}`,
      commentData,
      config(accessToken)
    );
    return data?.data;
  } catch (error) {
    throw error;
  }
};

const addProgressionAnalysis = async (
  accessToken: string,
  clinicId: string,
  patientId: string,
  examData: {
    eye: string;
    examIds: Array<string>;
    externalExamIds: Array<string>;
  }
): Promise<{ id: string }> => {
  try {
    const { data } = await axios.post(
      `${webServiceUrl}/clinics/${clinicId}/patients/${patientId}/progression-analysis`,
      examData,
      config(accessToken)
    );
    return data?.data;
  } catch (error) {
    throw error;
  }
};

const getProgressionAnalysisProcessInfo = async (
  accessToken: string | null,
  clinicId: string,
  patientId: string,
  progressionAnalysisId: string,
  signal: AbortSignal
): Promise<GPAProcessInfoType> => {
  try {
    const { data } = await axios.get(
      `${webServiceUrl}/clinics/${clinicId}/patients/${patientId}/progression-analysis/${progressionAnalysisId}/process-info`,
      config(accessToken, signal)
    );
    return data?.data;
  } catch (error) {
    throw error;
  }
};

const getProgressionAnalysisData = async (
  accessToken: string | null,
  clinicId: string,
  patientId: string,
  progressionAnalysisId: string,
  signal: AbortSignal
): Promise<string> => {
  try {
    const { data } = await axios.get(
      `${webServiceUrl}/clinics/${clinicId}/patients/${patientId}/progression-analysis/${progressionAnalysisId}/analysis-data`,
      config(accessToken, signal)
    );
    return data?.data;
  } catch (error) {
    throw error;
  }
};

const getProgressionAnalysisTrendlines = async (
  accessToken: string | null,
  clinicId: string,
  patientId: string,
  progressionAnalysisId: string,
  signal: AbortSignal
): Promise<string> => {
  try {
    const { data } = await axios.get(
      `${webServiceUrl}/clinics/${clinicId}/patients/${patientId}/progression-analysis/${progressionAnalysisId}/trendlines`,
      config(accessToken, signal)
    );
    return data?.data;
  } catch (error) {
    throw error;
  }
};

const addPositionalNote = async (
  accessToken: string | null,
  clinicId: string,
  patientId: string,
  examId: string,
  imageId: string,
  noteData: { xPct: number; yPct: number; text: string },
  signal: AbortSignal
): Promise<PositionalNoteType> => {
  try {
    const { data } = await axios.post(
      `${webServiceUrl}/clinics/${clinicId}/patients/${patientId}/external-exams/${examId}/images/${imageId}/notes`,
      noteData,
      config(accessToken, signal)
    );
    return data?.data;
  } catch (error) {
    throw error;
  }
};

const updatePositionalNote = async (
  accessToken: string | null,
  clinicId: string,
  patientId: string,
  examId: string,
  imageId: string,
  noteData: { id: string; xPct: number; yPct: number; text: string },
  signal: AbortSignal
): Promise<PositionalNoteType> => {
  try {
    const { data } = await axios.put(
      `${webServiceUrl}/clinics/${clinicId}/patients/${patientId}/external-exams/${examId}/images/${imageId}/notes/${noteData.id}`,
      noteData,
      config(accessToken, signal)
    );
    return data?.data;
  } catch (error) {
    throw error;
  }
};

const deletePositionalNote = async (
  accessToken: string | null,
  clinicId: string,
  patientId: string,
  examId: string,
  imageId: string,
  noteId: string,
  signal: AbortSignal
): Promise<PositionalNoteType> => {
  try {
    const { data } = await axios.delete(
      `${webServiceUrl}/clinics/${clinicId}/patients/${patientId}/external-exams/${examId}/images/${imageId}/notes/${noteId}`,
      config(accessToken, signal)
    );
    return data?.data;
  } catch (error) {
    throw error;
  }
};

export {
  // exam functions
  getExams,
  getExamById,
  getRecentlyCompletedExams,
  getScheduledExams,
  getLinkedExams,
  addExam,
  updateExam,
  deleteExam,
  transferExam,
  getExamAccessInfo,
  generateExamReportFile,
  getExamReportInfo,
  addExamReportComment,
  updateExamReportComment,

  // progression analysis functions
  getAllProgressionAnalyses,
  getRecentlyCompletedProgressionAnalyses,
  getProgressionAnalysisById,
  getProgressionAnalysisForExternalExams,
  deleteProgressionAnalysis,
  getProgressionAnalysisForExams,
  getProgressionAccessInfo,
  generateProgressionReportFile,
  getProgressionReportInfo,
  getProgressionAnalysisComments,
  addProgressionAnalysisComment,
  updateProgressionAnalysisComment,
  addProgressionAnalysis,
  getProgressionAnalysisProcessInfo,
  getProgressionAnalysisData,
  updateProgressionBaseline,
  getProgressionAnalysisTrendlines,

  // external exam functions
  getExternalExams,
  getProgressionExams,
  getProgressionExternalExams,
  getExternalExamById,
  deleteExternalExam,
  favoriteExternalExam,
  addPositionalNote,
  updatePositionalNote,
  deletePositionalNote,

  // report functions
  getReportSectionData,
  getExternalExamCharts,
  getReportSectionComments,
};
